<template>
  <div class="onboarding bg-black">
    <div class="embla" ref="emblaRef">
      <div class="embla__container">
        <div class="embla__slide" v-for="slide in slides" :key="slide.title">
          <div class="image-container" :class="{ 'last-slide': isLastSlide }">
            <!-- <img :src="slide.image" /> -->
            <video
              class="hero-loop"
              autoplay
              muted
              playsinline
              loop
              preload="metadata"
              :poster="slide.image">
              <source :src="slide.video" type="video/webm" />
            </video>
            <!-- <div class="card-bg">
              <img v-if="isCardSlide" :src="cardBg" />
            </div> -->
          </div>

          <div class="title" v-html="$t(slide.title)" />
          <div class="subtitle">
            {{ $t(slide.subtitle) }}
          </div>
        </div>
      </div>

      <div class="slider-dots-container">
        <SliderDots
          :total="slides.length"
          :active-index="selectedIndex"
          @select="handleDotSelect" />
      </div>
    </div>
    <BaseButton v-if="!isLastSlide" class="skip-button" size="sm" type="shade" @click="handleSkip">
      {{ $t("other|onboarding|skip") }}
    </BaseButton>
    <BaseButton
      class="next-button"
      :type="isLastSlide ? 'cta' : 'monochrome'"
      @click="handleNextButton">
      {{ isLastSlide ? $t("other|onboarding|toWallet") : $t("other|onboarding|next") }}
    </BaseButton>
    <button class="embla__prev" @click="scrollPrev" />
    <button class="embla__next" @click="scrollNext" />
  </div>
</template>

<script setup>
import emblaCarouselVue from "embla-carousel-vue";
import BaseButton from "@emcd-shared/base-button";
// import cardBg from "../../../public/images/onboarding/bg-card.svg";
import { useUiStore } from "@/stores/ui";
import { onBeforeUnmount, onMounted, ref, watch, computed } from "vue";
import SliderDots from "./SliderDots.vue";
import { useOnboardingStore } from "@/stores/onboarding";

const [emblaRef, emblaApi] = emblaCarouselVue({
  loop: false,
  align: "start",
  containScroll: "keepSnaps",
  skipSnaps: false,
  dragFree: false,
  inViewThreshold: 0.7,
});
const uiStore = useUiStore();
const {
  setIsDashboardOnboardingSkipped,
  setIsAppsOnboardingSkipped,
  setIsProfileOnboardingSkipped,
  setIsP2POnboardingSkipped,
  setIsEarnOnboardingSkipped,
} = useOnboardingStore();

const selectedIndex = ref(0);
const isLastSlide = ref(false);
const isCardSlide = ref(false);
const autoTimer = ref(null);
const isOnboarding = computed(() => uiStore.getIsOnboarding());

// Показ онбординга определяем сразу, чтобы разметка карусели монтировалась до onMounted
// if (!uiStore.getIsOnboarding()) {
//   uiStore.setIsOnboarding(true);
// }
const slides = [
  {
    title: "other|onboarding|p2p",
    subtitle: "other|onboarding|p2pDescription",
    image: "/images/onboarding/1.png",
    video: "/images/onboarding/p2p_webm.webm",
  },
  {
    title: "other|onboarding|multiwallet",
    subtitle: "other|onboarding|multiwalletDescription",
    image: "/images/onboarding/2.png",
    video: "/images/onboarding/multiwallet_webm.webm",
  },
  {
    title: "other|onboarding|passiveIncome",
    subtitle: "other|onboarding|passiveIncomeDescription",
    image: "/images/onboarding/3.png",
    video: "/images/onboarding/passive_webm.webm",
  },
  {
    title: "other|onboarding|cryptoCard",
    subtitle: "other|onboarding|cryptoCardDescription",
    image: "/images/onboarding/4.png",
    video: "/images/onboarding/2_webm.webm",
  },
  {
    title: "other|onboarding|security",
    subtitle: "other|onboarding|securityDescription",
    image: "/images/onboarding/5.png",
    // video: "/images/onboarding/2_webm.webm",
  },
];

function scrollNext() {
  emblaApi.value?.scrollNext();
}

function scrollPrev() {
  emblaApi.value?.scrollPrev();
}

function handleNextButton() {
  if (isLastSlide.value) {
    handleSkip();
  } else {
    scrollNext();
  }
}

function handleSkip() {
  localStorage.setItem("isOnboardingSkipped", true);
  localStorage.setItem("isDashboardOnboardingSkipped", false);
  setIsDashboardOnboardingSkipped(false);
  setIsAppsOnboardingSkipped(false);
  setIsProfileOnboardingSkipped(false);
  setIsP2POnboardingSkipped(false);
  setIsEarnOnboardingSkipped(false);
  uiStore.setIsOnboarding(false);
}

function handleDotSelect(index) {
  emblaApi.value?.scrollTo?.(index);
}

function scheduleAuto() {
  if (autoTimer.value) {
    clearTimeout(autoTimer.value);
    autoTimer.value = null;
  }
  const idx = emblaApi.value?.selectedScrollSnap?.();
  isLastSlide.value = idx === slides.length - 1;
  isCardSlide.value = idx === slides.length - 2;
  if (!isLastSlide.value) {
    autoTimer.value = setTimeout(() => {
      emblaApi.value?.scrollNext();
    }, 6000);
  }
}

function stopAuto() {
  if (autoTimer.value) {
    clearTimeout(autoTimer.value);
    autoTimer.value = null;
  }
}

function syncSelected() {
  const idx = emblaApi.value?.selectedScrollSnap?.();
  if (typeof idx === "number") selectedIndex.value = idx;
  scheduleAuto();
}

onMounted(() => {
  autoTimer.value = null;
  if (emblaApi.value) {
    syncSelected();
    emblaApi.value.on("init", syncSelected);
    emblaApi.value.on("select", syncSelected);
    emblaApi.value.on("reInit", syncSelected);
    emblaApi.value.on("settle", syncSelected);
    emblaApi.value.on("pointerDown", stopAuto);
    emblaApi.value.on("pointerUp", syncSelected);
    emblaApi.value.on("pointerUp", () => {
      // защита от перелёта: если Embla по инерции ушёл слишком далеко — вернём к ближайшему снапу
      const toIdx = emblaApi.value?.target?.()?.index;
      if (typeof toIdx === "number") {
        const clamped = Math.max(0, Math.min(slides.length - 1, toIdx));
        if (toIdx !== clamped) {
          emblaApi.value?.scrollTo?.(clamped);
        }
      }
    });
  }
  onBeforeUnmount(() => {
    if (autoTimer.value) clearTimeout(autoTimer.value);
  });
});

watch(isOnboarding, async (value) => {
  emblaApi.value?.scrollTo?.(0);
  if (value) {
    clearTimeout(autoTimer.value);
    autoTimer.value = null;
  }
});
</script>

<style lang="scss" scoped>
.onboarding {
  position: absolute;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  overflow: auto;
  z-index: 1000;
  background: #000;
}

.image-container {
  width: 100%;
  height: 280px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  img {
    width: 90%;
    height: 100%;
    object-fit: contain;
  }

  video {
    width: 90%;
    height: 100%;
    object-fit: contain;
  }
}

.skip-button {
  position: absolute; // своя кнопка на каждом слайде
  top: 15px;
  right: 16px;
  z-index: 1001; // выше зон prev/next и другого контента онбординга
  --base-button-width-default: fit-content;
}

.next-button {
  position: fixed;
  --base-button-width-default: calc(100% - 32px);
  margin: 0 16px;
  bottom: 30px;
  transition: all 0.7s ease;
}

.embla {
  position: relative;
  width: 100%;
  height: calc(100% - 50px);
  height: 100%;
  overflow: hidden;
  animation: onboardingFadeIn 600ms ease-out both;
}

@keyframes onboardingFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.embla__container {
  display: flex;
  height: 100%;
}

.embla__slide {
  flex: 0 0 100%;
  min-width: 0;
  height: 100%;
  margin-right: 4px;
}

.embla__prev {
  position: absolute;
  width: 76px; // уже, чтобы не блокировала свайпы
  height: 64%;
  top: 15%;
  left: 0;
  z-index: 2;
}

.embla__next {
  position: absolute;
  width: 76px; // уже, чтобы не блокировала свайпы
  height: 64%;
  top: 15%;
  right: 0;
  z-index: 2;
}

.slider-dots-container {
  position: absolute;
  bottom: 96px;
  left: 0;
  width: 100%;
}

.title {
  margin-bottom: 12px;
  padding: 0 30px;
  color: var(--color-1-text, #fafafa);
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  line-height: 135%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.subtitle {
  margin-bottom: 104px;
  padding: 0 30px;
  color: var(--color-4-text, #71727c);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.last-slide {
  video {
    width: 80%;
    height: 90%;
  }
}

// .card-bg {
//   position: absolute;
//   bottom: 0;
//   img {
//     width: 100%;
//     height: 100%;
//     object-fit: contain;
//   }
// }
</style>
